import React, { useEffect, useState } from 'react';
import 'bootstrap-select';
import { useSelector } from 'react-redux';
import InstallerService from 'service/InstallerService';
import { Link } from 'react-router-dom';
import LoadingSpinner from 'component/LoadingSpinner';
import { hasInstallerAccess } from '../../helpers/hasPrivilege';
import UserDetailModal from '../../component/userDetailModal/UserViewModal';
import { BaseResponse } from 'domain/payload/response/BaseResponseDto';
import CustomDropdown from 'component/CustomDropdown';
import { PageSizeOption } from 'domain/constant/PageSizeOption';

const Installer: React.FC = () => {
  const { profile } = useSelector((state: any) => state.auth);
  const [data, setData] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingExportCsv, setLoadingExportCsv] = useState(false);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const canCreate = hasInstallerAccess(profile, 'create');
  const canUpdate = hasInstallerAccess(profile, 'update');
  const canDelete = hasInstallerAccess(profile, 'delete');

  const installerService = InstallerService();

  const fetchData = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setLoading(true);
    try {
      const res: BaseResponse<any[]> = await installerService.installerList(token, searchTerm, currentPage, pageSize);
      console.log('Installer list response:', res);
      setData(res.data ?? []);
      setTotalPages(res.pagination?.totalPage ?? 0);
      setTotalRecords(res.pagination?.totalRecords ?? 0);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil data installer';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize]);

  const handleSearchClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchData();
  };

  const handleExportToCsv = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setLoadingExportCsv(true);
    try {
      const response = await installerService.installerExportToCsv(token);
      window.alert(response?.message || 'File export sedang diproses dan akan dikirimkan ke email Anda.');
    } catch (error: any) {
      console.error("Error exporting CSV:", error);
      const errorMessage = error.response?.data?.message || error.message || error || 'Terjadi kesalahan saat export CSV';
      window.alert(`Error export CSV: ${errorMessage}`);
    } finally {
      setLoadingExportCsv(false);
    }
  };

  const handleDelete = async (id: string) => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    if (!window.confirm("Apakah Anda yakin ingin menghapus Field User ini?")) return;

    try {
      setLoading(true);
      await installerService.installerDelete(token, id);
      window.alert('Field User berhasil dihapus');
      fetchData();
    } catch (err: any) {
      console.error("Error deleting Field User:", err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat menghapus Field User';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = (id: string) => {
    setSelectedUserId(id);
    setShowDetailModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedUserId(null);
  };

  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="d-flex">
            <h5 style={{ flex: 1 }}>Field Users</h5>
            <div>
              <p>TransMore Field Users</p>
            </div>
          </div>
        </div>
        <div className="col-12">

          <div className="p-4" style={{ background: 'white' }}>
            <div className="mb-3 d-flex flex-nowrap gap-2 overflow-auto text-nowrap">

              <form className="d-flex" onSubmit={handleSearchClick}>
                <input
                  type="text"
                  className="form-control form-control-compact me-2"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ minWidth: 120 }}
                />
                <button type="submit" className="px-4 btn btn-primary">
                  <i className="fa-solid fa-search"></i>
                </button>
              </form>

              <div className="d-flex justify-content-end" style={{ flex: 1 }}>
                {canCreate && (
                  <>
                    <button className="btn btn-outline-primary me-2">
                      Import From CSV
                    </button>
                  </>
                )}
                <button className="btn btn-outline-primary me-2 d-flex justify-content-center align-items-center" onClick={handleExportToCsv} disabled={loadingExportCsv}>
                  {
                    loadingExportCsv &&
                    <>
                      <LoadingSpinner />
                      <p className="ms-2"></p>
                    </>
                  }
                  Export To CSV
                </button>
                {canCreate && (
                  <>
                    <Link to="/installer/0" className='float-end text-decoration-none tm-text-primary'>
                      <button className="btn btn-primary">
                        <span className="me-2">
                          Add
                        </span>
                        <i className="fa-solid fa-plus"></i>
                      </button>
                    </Link>
                  </>
                )}
              </div>
            </div>

            <table className="table table-condensed table-hover">
              <thead className="table-primary">
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Client</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Created By</th>
                  <th>Joined At</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {
                  !loading ?
                    data.length > 0 ? (
                      data.map((item) => (
                        <tr key={item.id}>
                          <td>{item.id}</td>
                          <td>{item.name}</td>
                          <td>{item.email}</td>
                          <td>{item.phone}</td>
                          <td>{item.clientName}</td>
                          <td>{item.roles?.map((r: any) => r.name).join(', ')}</td>
                          <td>{item.active ? 'Active' : 'Not Active'}</td>
                          <td>{item.createdBy}</td>
                          <td>{item.createdAt}</td>
                          <td>
                            <div className="d-flex">
                              <button
                                className="border-0 btn btn-outline-info btn-small-compact d-flex align-items-center justify-content-center"
                                onClick={() => handleViewDetail(item.id)}
                                title="View Details"
                                style={{ width: '32px', height: '32px' }}
                              >
                                <i className="fa-solid fa-eye"></i>
                              </button>

                              {canUpdate && (
                                <>
                                  <Link to={'/installer/' + item.id}>
                                    <button className="border-0 btn btn-outline-warning btn-small-compact ms-2">
                                      <i className="fa-solid fa-edit"></i>
                                    </button>
                                  </Link>
                                </>
                              )}
                              {canDelete && (
                                <>
                                  <button
                                    className="border-0 btn btn-outline-danger btn-small-compact ms-2"
                                    onClick={() => handleDelete(item.id)}
                                  >
                                    <i className="fa-solid fa-trash"></i>
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={10} className="text-center">
                          Tidak ada data
                        </td>
                      </tr>
                    )
                    :
                    <tr>
                      <td colSpan={10} className="text-center">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </td>
                    </tr>
                }
              </tbody>
            </table>
            <div className="mt-3 d-flex justify-content-end align-items-center">
              <div className="d-flex align-items-center">
                <div className="me-4 d-flex align-items-center">
                  <label className="me-2">Rows per page:</label>
                  <CustomDropdown
                    value={pageSize}
                    options={PageSizeOption.options}
                    onChange={(opt) => {
                      setPageSize(Number(opt.id));
                      setCurrentPage(1);
                    }}
                    align="top"
                  />
                </div>
                <span className="me-3">
                  {`${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords}`}
                </span>

                <nav>
                  <ul className="mb-0 pagination">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-left"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-left"></i>
                      </button>
                    </li>

                    <li className="page-item disabled">
                      <span className="page-link" style={{ border: 0 }}>{currentPage}</span>
                    </li>

                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-right"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(totalPages)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>

          </div>
        </div>
      </div>

      <UserDetailModal
        show={showDetailModal}
        onClose={handleCloseModal}
        userId={selectedUserId || undefined}
        token={profile?.data.token}
      />
    </div>
  );
};

export default Installer;